import * as React from 'react';
import classnames from 'classnames';
import { kebabCase, snakeCase } from 'lodash';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import StatusLabel from 'components/status-label/StatusLabel';
import AddNew from 'components/buttons/add-new/AddNew';
import Tooltip from 'components/Tooltip';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { isGreaterThanZero, isNotEmpty, isRequired } from 'utils/validators';

import './campaign-payouts-installments-modal-fields.scss';

import type {
  Data,
  MixedSelectDropdownItem,
  Payout,
  SubcampaignInfluencerProfile
} from 'types';

applyTranslations(require('./campaign-payouts-installments-modal-fields.i18n.json'));

const FIELD_TYPES = ['note', 'amount', 'scheduledDate', 'icon'];

type FieldType = HTMLInputElement | HTMLSelectElement;

type Props = {
  influencerProfile: SubcampaignInfluencerProfile | undefined | null,
  fieldKey: string,
  payouts: Array<Payout>,
  dropdownDates: Array<string>,
  isLoading: boolean,
  isEditMode: boolean,
  onFieldChange: (event: React.SyntheticEvent<FieldType>) => void,
  onDateChange: (value: string, position: number) => void,
  onAddField: () => void,
  onRemoveField: (position: number) => void
};

const MSG_KEY = 'campaign_payouts_installments_modal_fields';

class CampaignPayoutsInstallmentsModalFields extends React.PureComponent<Props> {
  getDropdownItems = (payout: Payout): Array<MixedSelectDropdownItem> => {
    const dropdownDate = payout.scheduledDate || '';

    return this.props.dropdownDates.map(value => ({
      identifier: getDisplayDate(value),
      isDisabled: false,
      isSelected: dropdownDate === value,
      label: getDisplayDate(value),
      value: getDisplayDate(value)
    }));
  };

  handleIconClick = (event: React.SyntheticEvent<HTMLSpanElement>): void => {
    const { onAddField, onRemoveField } = this.props;
    const target = event.currentTarget;
    const name = target.dataset.name || '';
    const position = Number(target.dataset.position) || 0;

    if (name === 'add') {
      onAddField();
    }

    if (name === 'remove') {
      onRemoveField(position);
    }
  };

  handleDateChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { onDateChange } = this.props;

    onDateChange(
      selectedItem?.identifier || updatedItems[0].identifier,
      data?.position
    );
  };

  renderHeader(key: string): React.ReactNode {
    const headerClasses = classnames('campaign-payouts-installments-modal-fields__header', {
      [`campaign-payouts-installments-modal-fields__${kebabCase(key)}`]: true
    });

    return (
      <div
        key={key}
        className={headerClasses}
      >
        <div>
          {
            key !== 'icon' &&
              <React.Fragment>
                <span>{__t(`${MSG_KEY}.labels.${snakeCase(key)}`)}</span>
                <span className="campaign-payouts-installments-modal-fields__required">
                  *
                </span>
              </React.Fragment>
          }
        </div>
        {
          key === 'scheduledDate' && (
            <Tooltip
              message={__t(`${MSG_KEY}.tooltips.release_deadline`)}
              classNameWrapper="campaign-payouts-installments-modal-fields__tooltip-wrapper"
              className="campaign-payouts-installments-modal-fields__tooltip"
              showQuestionMark
            />
          )
        }
      </div>
    );
  }

  renderDropdown = (
    payout: Payout,
    position: number
  ): React.ReactNode => {
    const dropdownItems = this.getDropdownItems(payout);

    return (
      <MixedSelectDropdown
        key={`campaign-payouts-installments-modal-fields__dropdown--${position}`}
        name="campaign-payouts-installments-modal-fields__dropdown"
        items={dropdownItems}
        placeholder={__t(`${MSG_KEY}.placeholders.date_dropdown`)}
        withSelectedInList
        className="campaign-payouts-installments-modal-fields__dropdown"
        data={{ position }}
        onItemsToggle={this.handleDateChange}
      />
    );
  };

  renderFields = (payout: Payout, position: number): JSX.Element => {
    const { payouts, influencerProfile, fieldKey, isEditMode, onFieldChange } = this.props;
    const dropdownDate = payout.scheduledDate || '';
    const influencerName = influencerProfile && (influencerProfile.artistName || influencerProfile.name) || '';

    return (
      <React.Fragment key={`${fieldKey}-${position}`}>
        <div className="campaign-payouts-installments-modal-fields__section">
          <div className="campaign-payouts-installments-modal-fields__note">
            <FormField
              direction="column"
              showValidationIcon={false}
              validators={[
                isNotEmpty(payout.note),
                isRequired(payout.note)
              ]}
            >
              {
                !payout.releaseDate ?
                  <Input
                    type="text"
                    name="note"
                    width="responsive"
                    value={payout.note}
                    maxLength={100}
                    data={{ position }}
                    placeholder={__t(`${MSG_KEY}.placeholders.notes_field`)}
                    onChange={onFieldChange}
                  /> :
                  <div>
                    {payout.note}
                  </div>
              }
            </FormField>
          </div>
          <div className="campaign-payouts-installments-modal-fields__amount">
            <FormField
              direction="column"
              showValidationIcon={false}
              validators={[
                isGreaterThanZero(payout.amount)
              ]}
            >
              {
                !payout.releaseDate ?
                  <Input
                    type="number"
                    name="amount"
                    min={0}
                    step={1}
                    width="responsive"
                    value={String(payout.amount)}
                    data={{ position }}
                    className="campaign-payouts-installments-modal-fields__number"
                    onChange={onFieldChange}
                  /> :
                  <div className="campaign-payouts-installments-modal-fields__number">
                    {renderPrice(payout.amount, { separateThousands: false })}
                  </div>
              }
            </FormField>
          </div>
          <div className="campaign-payouts-installments-modal-fields__scheduled-date">
            <FormField
              direction="column"
              showValidationIcon={false}
              validators={[
                isRequired(payout.scheduledDate)
              ]}
            >
              {
                !payout.releaseDate
                  ? this.renderDropdown(payout, position)
                  : (
                    <div>
                      {getDisplayDate(dropdownDate)}
                    </div>
                  )
              }
            </FormField>
          </div>
          <div className="campaign-payouts-installments-modal-fields__icon">
            {
              (!payout.releaseDate && (isEditMode || payouts.length > 1)) && (
                <div
                  data-name="remove"
                  data-position={position}
                  data-qa="campaign-payments-installments-modal-icon--remove"
                  onClick={this.handleIconClick}
                >
                  <SVGDynamicIcon
                    key={`${influencerName}-${position}-remove`}
                    name="cross-custom-fg"
                    width={8}
                    height={8}
                    customColor="grey"
                    clickable
                  />
                </div>
              )
            }
            {
              payout.executed && (
                <div className="campaign-payouts-installments-modal-fields__icon--paid">
                  <StatusLabel
                    label={__t(`${MSG_KEY}.labels.paid`)}
                    size="small"
                  />
                </div>
              )
            }
          </div>
        </div>
      </React.Fragment>
    );
  };

  render(): React.ReactNode {
    const { payouts, isLoading } = this.props;

    return (
      <section className="campaign-payouts-installments-modal-fields">
        <div className="campaign-payouts-installments-modal-fields__headers">
          {
            FIELD_TYPES.map(header => (
              this.renderHeader(header)
            ))
          }
        </div>
        {
          isLoading ?
            <div className="campaign-payouts-installments-modal-fields__spinner">
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div> :
            payouts.map((payout, index) => (
              this.renderFields(payout, index)
            ))
        }
        {
          !isLoading && (
            <div className="campaign-payouts-installments-modal-fields__add-new">
              <AddNew
                icon={{
                  name: 'plus-inside-circle-custom-bg-white-fg',
                  width: 16,
                  height: 16
                }}
                text={__t(`${MSG_KEY}.labels.add_installments`)}
                data={{ name: 'add' }}
                onClick={this.handleIconClick}
              />
            </div>
          )
        }
      </section>
    );
  }
}

export default CampaignPayoutsInstallmentsModalFields;
